/**
 * AI Scribe V2 — session body
 *
 * Everything below the window header: the three live columns (transcript /
 * clinical events / instructions) while recording, and the draft-note preview
 * once the session is finished. Purely presentational — all data and callbacks
 * arrive as props.
 */

import Flex from "@/components/Flex";
import { px } from "@/tokens/design-tokens";
import { ClinicalNotesDTO } from "@/services";
import { VisitNote } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/ActiveForm";
import { SCRIBE_INSTRUCTIONS_EDITABLE_STATUSES } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/ai-scriber/ai-scriber-constant";
import { SCRIBE_V2_COLORS, SCRIBE_V2_DIMENSIONS } from "../constants";
import {
  ClinicalEvent,
  ScribeInstructions,
  ScribeStatus,
  ScribeTranscriptLine,
} from "../types";
import ClinicalEventsPanel from "./ClinicalEventsPanel";
import LiveTranscriptPanel from "./LiveTranscriptPanel";
import ScribeDraftPanel from "./ScribeDraftPanel";
import ScribeInstructionsPanel from "./ScribeInstructionsPanel";

export interface ScribeSessionBodyProps {
  isMinimized: boolean;
  isDrafting: boolean;
  noteType: VisitNote;
  draftSegments?: ScribeTranscriptLine[];
  onNoteReceived: (data: ClinicalNotesDTO) => void;
  onRetryNoteGeneration: () => void;
  sessionStartedAt: number;
  transcript: ScribeTranscriptLine[];
  status: ScribeStatus;
  onTogglePause: () => void;
  clinicalEvents: ClinicalEvent[];
  instructions: ScribeInstructions;
  onInstructionsChange: (value: ScribeInstructions) => void;
  onFinish: () => void;
}

const ScribeSessionBody = ({
  isMinimized,
  isDrafting,
  noteType,
  draftSegments,
  onNoteReceived,
  onRetryNoteGeneration,
  sessionStartedAt,
  transcript,
  status,
  onTogglePause,
  clinicalEvents,
  instructions,
  onInstructionsChange,
  onFinish,
}: ScribeSessionBodyProps) => {
  if (isMinimized) return null;

  if (isDrafting)
    return (
      <ScribeDraftPanel
        noteType={noteType}
        transcriptionSegments={draftSegments ?? []}
        onNoteReceived={(data) => onNoteReceived(data as ClinicalNotesDTO)}
        onRetryNoteGeneration={onRetryNoteGeneration}
        sessionStartedAt={sessionStartedAt}
      />
    );

  return (
    <Flex
      sx={{
        flex: 1,
        minHeight: 0,
        overflowX: "auto",
        backgroundColor: SCRIBE_V2_COLORS.panelBg,
      }}
    >
      <Flex
        sx={{
          flex: SCRIBE_V2_DIMENSIONS.transcriptFlex,
          minWidth: px(SCRIBE_V2_DIMENSIONS.transcriptMinWidth),
        }}
      >
        <LiveTranscriptPanel
          transcript={transcript}
          status={status}
          onTogglePause={onTogglePause}
        />
      </Flex>

      <Flex
        sx={{
          flex: SCRIBE_V2_DIMENSIONS.eventsFlex,
          minWidth: px(SCRIBE_V2_DIMENSIONS.eventsMinWidth),
        }}
      >
        <ClinicalEventsPanel events={clinicalEvents} />
      </Flex>

      <Flex
        sx={{
          flex: SCRIBE_V2_DIMENSIONS.instructionsFlex,
          minWidth: px(SCRIBE_V2_DIMENSIONS.instructionsMinWidth),
        }}
      >
        <ScribeInstructionsPanel
          instructions={instructions}
          onChange={onInstructionsChange}
          isEditable={SCRIBE_INSTRUCTIONS_EDITABLE_STATUSES.some(
            (editableStatus) => editableStatus === status
          )}
          onFinish={onFinish}
          isFinishDisabled={transcript.length === 0}
        />
      </Flex>
    </Flex>
  );
};

export default ScribeSessionBody;
